import React, { useState, useContext } from "react";
import axios from "axios";
import { useAlert } from "../../../AlertContext";
import LoadingCircle from "../../../LoadingCircle";
import { InfoContext } from "../../../InfoContext";
import { AuthContext } from "../../../AuthContext";

const server_url = process.env.REACT_APP_BACKEND_URL;

const AddSeasonForm: React.FC = () => {
  const [nameSeason, setNameSeason] = useState("");
  const [loadingSeason, setLoadingSeason] = useState(false);
  const { session } = useContext(AuthContext);
  const { fetchSeasons } = useContext(InfoContext);
  const { showAlert } = useAlert();

  const handleSeasonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoadingSeason(true);
      const response = await axios.post(`${server_url}/addSeason`, {
        access_token: session.access_token,
        name: nameSeason,
      });
      if (response.data.status) {
        showAlert("success", "Season added!");
        await fetchSeasons();
      } else {
        // console.log('Error adding Season: ', response);
        showAlert("error", response.data.error);
      }
    } catch (error) {
      // console.log('Error adding Season: ', error);
      showAlert("error", "Error adding Season");
    } finally {
      setNameSeason("");
      setLoadingSeason(false);
    }
  };

  return (
    <form
      onSubmit={handleSeasonSubmit}
      className="p-6 bg-white rounded-xl shadow-md w-full max-w-sm"
    >
      <h2 className="mb-4 text-2xl font-semibold text-center">Add Season</h2>
      <div className="mb-4">
        <label className="block mb-1 font-semibold">Season name</label>
        <input
          type="text"
          className="w-full px-3 py-2 border rounded-xl"
          value={nameSeason}
          onChange={(e) => setNameSeason(e.target.value)}
          required
        />
      </div>
      <button
        type="submit"
        className={`w-full py-2 h-10 font-semibold text-white bg-blue-500 rounded-xl hover:bg-blue-600 ${
          loadingSeason ? "hover:bg-blue-500" : ""
        } `}
        disabled={loadingSeason}
      >
        {!loadingSeason ? <p>Add Season</p> : <LoadingCircle small={true} />}
      </button>
    </form>
  );
};

const CreateGroupsForm: React.FC = () => {
  const [seasonId, setSeasonId] = useState("");
  const [loadingGroup, setLoadingGroup] = useState(false);
  const { session } = useContext(AuthContext);
  const { seasons, fetchSeasons } = useContext(InfoContext);
  const { showAlert } = useAlert();

  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoadingGroup(true);
      const response = await axios.post(`${server_url}/createGroups`, {
        access_token: session.access_token,
        season_id: seasonId,
      });
      if (response.data.status) {
        showAlert("success", "Groups created!");
        await fetchSeasons();
      } else {
        // console.log('Error creating groups: ', response);
        showAlert("error", response.data);
      }
    } catch (error) {
      // console.log('Error creating groups: ', error);
      showAlert("error", "Error creating groups");
    } finally {
      setLoadingGroup(false);
      setSeasonId("");
    }
  };

  return (
    <form
      onSubmit={handleGroupSubmit}
      className="flex flex-col p-6 bg-white rounded-xl shadow-md w-full max-w-sm gap-y-4"
    >
      <h2 className="text-2xl font-semibold text-center">Create Groups</h2>
      <select
        id="seasonSelect"
        value={seasonId}
        onChange={(e) => setSeasonId(e.target.value)}
        className="w-full px-3 py-2 border rounded-xl "
        required
      >
        <option value="" disabled>
          Select a season
        </option>
        {seasons
          .filter((season) => season.groups.length === 0)
          .map((season) => (
            <option key={season.id} value={season.id}>
              {season.name}
            </option>
          ))}
      </select>
      <button
        type="submit"
        className={`w-full py-2 h-10 font-semibold text-white bg-blue-500 rounded-xl hover:bg-blue-600 ${
          loadingGroup ? "hover:bg-blue-500" : ""
        } `}
        disabled={loadingGroup}
      >
        {!loadingGroup ? <p>Create Groups</p> : <LoadingCircle small={true} />}
      </button>
    </form>
  );
};

const CreateMatchesForm: React.FC = () => {
  const [selectedCreateMatchesSeasonId, setSelectedCreateMatchesSeasonId] =
    useState("");
  const [loadingCreateMatches, setLoadingCreateMatches] = useState(false);
  const { session } = useContext(AuthContext);
  const { seasons, fetchSeasons } = useContext(InfoContext);
  const { showAlert } = useAlert();

  const handleCreateMatchesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoadingCreateMatches(true);
      const response = await axios.post(`${server_url}/createMatches`, {
        access_token: session.access_token,
        season_id: selectedCreateMatchesSeasonId,
      });
      if (response.data.status) {
        showAlert("success", "Matches created!");
        await fetchSeasons();
      } else {
        // console.log('Error creating matches: ', response);
        showAlert("error", response.data);
      }
    } catch (error) {
      // console.log('Error creating matches: ', error);
      showAlert("error", "Error creating matches");
    } finally {
      setLoadingCreateMatches(false);
      setSelectedCreateMatchesSeasonId("");
    }
  };

  return (
    <form
      onSubmit={handleCreateMatchesSubmit}
      className="flex flex-col p-6 bg-white rounded-xl shadow-md w-full max-w-sm gap-y-4"
    >
      <h2 className="text-2xl font-semibold text-center">Create Matches</h2>
      <select
        id="seasonSelect"
        value={selectedCreateMatchesSeasonId}
        onChange={(e) => setSelectedCreateMatchesSeasonId(e.target.value)}
        className="w-full px-3 py-2 border rounded-xl "
        required
      >
        <option value="" disabled>
          Select a season
        </option>
        {seasons.map((season) => (
          <option key={season.id} value={season.id}>
            {season.name}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className={`w-full py-2 h-10 font-semibold text-white bg-blue-500 rounded-xl hover:bg-blue-600 ${
          loadingCreateMatches ? "hover:bg-blue-500" : ""
        } `}
        disabled={loadingCreateMatches}
      >
        {!loadingCreateMatches ? (
          <p>Create Matches</p>
        ) : (
          <LoadingCircle small={true} />
        )}
      </button>
    </form>
  );
};

const DeleteSeasonForm: React.FC = () => {
  const [selectedDeleteSeasonId, setSelectedDeleteSeasonId] = useState("");
  const [loadingDeleteSeason, setLoadingDeleteSeason] = useState(false);
  const { session } = useContext(AuthContext);
  const { seasons, fetchSeasons } = useContext(InfoContext);
  const { showAlert } = useAlert();

  const handleDeleteSeasonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      window.confirm(
        "Are you sure you want to delete this Season? This action cannot be undone."
      )
    ) {
      try {
        setLoadingDeleteSeason(true);
        const response = await axios.post(`${server_url}/deleteSeason`, {
          access_token: session.access_token,
          season_id: selectedDeleteSeasonId,
        });
        if (response.data.status) {
          showAlert("success", "Season deleted!");
          await fetchSeasons();
        } else {
          // console.log('Error deleting season: ', response);
          showAlert("error", response.data);
        }
      } catch (error) {
        // console.log('Error deleting season: ', error);
        showAlert("error", "Error deleting season");
      } finally {
        setLoadingDeleteSeason(false);
        setSelectedDeleteSeasonId("");
      }
    }
  };

  return (
    <form
      onSubmit={handleDeleteSeasonSubmit}
      className="flex flex-col p-6 bg-white rounded-xl shadow-md w-full max-w-sm gap-y-4"
    >
      <h2 className="text-2xl font-semibold text-center">Delete Season</h2>
      <select
        id="seasonSelect"
        value={selectedDeleteSeasonId}
        onChange={(e) => setSelectedDeleteSeasonId(e.target.value)}
        className="w-full px-3 py-2 border rounded-xl "
        required
      >
        <option value="" disabled>
          Select a season
        </option>
        {seasons.map((season) => (
          <option key={season.id} value={season.id}>
            {season.name}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className={`w-full py-2 h-10 font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 ${
          loadingDeleteSeason ? "hover:bg-red-500" : ""
        } `}
        disabled={loadingDeleteSeason}
      >
        {!loadingDeleteSeason ? <p>Delete</p> : <LoadingCircle small={true} />}
      </button>
    </form>
  );
};

const RegenerateTableForm: React.FC = () => {
  const [selectedRegenerateTableSeasonId, setSelectedRegenerateTableSeasonId] =
    useState("");
  const [loadingRegenerateTable, setLoadingRegenerateTable] = useState(false);
  const { session } = useContext(AuthContext);
  const { seasons, fetchSeasons } = useContext(InfoContext);
  const { showAlert } = useAlert();

  const handleRegenerateTableSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      window.confirm(
        "Are you sure you want to regenerate the table of this Season? This action cannot be undone."
      )
    ) {
      try {
        setLoadingRegenerateTable(true);
        const response = await axios.post(
          `${server_url}/regenerateTablesOfSeason`,
          {
            access_token: session.access_token,
            season_id: selectedRegenerateTableSeasonId,
          }
        );
        if (response.data.status) {
          showAlert("success", "Table new!");
          await fetchSeasons();
        } else {
          // console.log('Error regerating table: ', response);
          showAlert("error", response.data);
        }
      } catch (error) {
        // console.log('Error regerating table: ', error);
        showAlert("error", "Error regerating table");
      } finally {
        setLoadingRegenerateTable(false);
        setSelectedRegenerateTableSeasonId("");
      }
    }
  };

  return (
    <form
      onSubmit={handleRegenerateTableSubmit}
      className="flex flex-col p-6 bg-white rounded-xl shadow-md w-full max-w-sm gap-y-4"
    >
      <h2 className="text-2xl font-semibold text-center">Regenerate Table</h2>
      <select
        id="seasonSelect"
        value={selectedRegenerateTableSeasonId}
        onChange={(e) => setSelectedRegenerateTableSeasonId(e.target.value)}
        className="w-full px-3 py-2 border rounded-xl "
        required
      >
        <option value="" disabled>
          Select a season
        </option>
        {seasons.map((season) => (
          <option key={season.id} value={season.id}>
            {season.name}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className={`w-full py-2 h-10 font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 ${
          loadingRegenerateTable ? "hover:bg-red-500" : ""
        } `}
        disabled={loadingRegenerateTable}
      >
        {!loadingRegenerateTable ? (
          <p>Regenerate</p>
        ) : (
          <LoadingCircle small={true} />
        )}
      </button>
    </form>
  );
};

export {
  AddSeasonForm,
  CreateGroupsForm,
  CreateMatchesForm,
  DeleteSeasonForm,
  RegenerateTableForm,
};
